import type { MedicalHistory } from '@/types/visit'
import { formatDate } from '@/lib/dates'
import { formatSex, formatGender } from '@/lib/format'

function InfoRow({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-sm text-slate-700">{value}</span>
    </div>
  )
}

interface MedicalHistoryPanelProps {
  medicalHistory: MedicalHistory | null
}

export default function MedicalHistoryPanel({ medicalHistory }: MedicalHistoryPanelProps) {
  if (!medicalHistory) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <h2 className="text-lg font-medium text-slate-900">Medical History</h2>
        <p className="mt-4 text-center text-sm text-slate-500">
          No medical history submitted yet.
        </p>
      </div>
    )
  }

  const address = [
    medicalHistory.address_line1,
    medicalHistory.address_line2,
    `${medicalHistory.city}, ${medicalHistory.state} ${medicalHistory.zip}`,
  ].filter(Boolean).join(', ')

  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-medium text-slate-900">Medical History</h2>
        {medicalHistory.updated_at && (
          <span className="text-xs text-slate-400">
            Updated {formatDate(medicalHistory.updated_at)}
          </span>
        )}
      </div>

      <div className="mt-4 space-y-6">
        <div>
          <h3 className="mb-2 text-sm font-medium text-slate-600">Demographics</h3>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <InfoRow label="Date of Birth" value={formatDate(medicalHistory.date_of_birth)} />
            <InfoRow label="Sex at Birth" value={formatSex(medicalHistory.sex_at_birth)} />
            <InfoRow label="Gender Identity" value={formatGender(medicalHistory.gender_identity)} />
            <InfoRow label="Phone" value={medicalHistory.phone} />
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-slate-600">Address</h3>
          <p className="text-sm text-slate-700">{address}</p>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-slate-600">Medical</h3>
          <div className="space-y-3">
            <InfoRow label="Allergies" value={medicalHistory.allergies} />
            <InfoRow label="Current Medications" value={medicalHistory.current_medications} />
            <InfoRow label="Medical Conditions" value={medicalHistory.medical_conditions} />
            <InfoRow label="Previous Skin Conditions" value={medicalHistory.previous_skin_conditions} />
            <InfoRow label="Family Skin History" value={medicalHistory.family_skin_history} />
            <InfoRow label="Skincare Routine" value={medicalHistory.skincare_routine} />
          </div>
        </div>

        {(medicalHistory.pregnancy_status || medicalHistory.breastfeeding_status) && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-slate-600">Reproductive</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <InfoRow label="Pregnancy Status" value={medicalHistory.pregnancy_status} />
              <InfoRow label="Breastfeeding Status" value={medicalHistory.breastfeeding_status} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
